import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { getPartnerId, getDisplayName } from '../lib/owner'
import { uploadMomentImage } from '../lib/momentsImageUpload'

// Moments: today's prompt + the pair's captures + live reveal.
//
// `promptIdArg` (optional): when passed, load that specific prompt
// instead of the latest. /moments/capture/:promptId passes this so
// the URL drives the row even if the user opens the push after
// midnight UTC (when the row still belongs to yesterday's KSA date).
//
// RLS does the heavy lifting:
//   - my own capture row is always returned
//   - partner's capture row is only returned after I've inserted
//     mine for the same prompt (the blur-until-you-submit gate)
//
// Realtime channels on moments_captures + moments_prompts keep both
// sides live with no polling: the partner image surfaces the moment
// they submit, and the prompt.status flip to 'revealed' arrives in
// the same stream.
export function useMomentsToday(promptIdArg = null, { locale = 'en' } = {}) {
  const { user } = useAuth()
  const userId = user?.id || null
  const partnerId = getPartnerId(user) || null

  const [prompt, setPrompt] = useState(null)
  const [captures, setCaptures] = useState([])
  const [loading, setLoading] = useState(true)
  const [signedUrls, setSignedUrls] = useState({})
  const [now, setNow] = useState(() => Date.now())
  const signingRef = useRef(new Set())

  const refetch = useCallback(async () => {
    if (!userId) {
      setPrompt(null)
      setCaptures([])
      return
    }
    let p = null
    if (promptIdArg) {
      const { data } = await supabase
        .from('moments_prompts')
        .select('*')
        .eq('id', promptIdArg)
        .maybeSingle()
      p = data || null
    } else {
      const { data } = await supabase
        .from('moments_prompts')
        .select('*')
        .order('prompt_date', { ascending: false })
        .limit(1)
        .maybeSingle()
      p = data || null
    }
    setPrompt(p)

    if (!p) { setCaptures([]); return }
    const { data: caps } = await supabase
      .from('moments_captures')
      .select('id, prompt_id, user_id, image_path, captured_at, late')
      .eq('prompt_id', p.id)
    setCaptures(caps || [])
  }, [userId, promptIdArg])

  useEffect(() => {
    if (!userId) { setLoading(false); return undefined }
    let alive = true
    setLoading(true)
    refetch().then(() => { if (alive) setLoading(false) })

    const channel = supabase
      .channel(`moments-${userId}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'moments_captures' },
        async () => { if (alive) await refetch() })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'moments_prompts' },
        async () => { if (alive) await refetch() })
      .subscribe()
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [userId, refetch])

  // Tick once a second so countdown text re-renders. Skips when there's
  // no prompt loaded so the page is otherwise quiet.
  useEffect(() => {
    if (!prompt) return undefined
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [prompt])

  // Sign URLs for visible capture paths. Cache by path, dedupe in-flight
  // requests via signingRef so realtime refetches don't trigger N
  // duplicate signing calls for the same row.
  useEffect(() => {
    let alive = true
    const todo = captures.filter((c) =>
      c.image_path && !signedUrls[c.image_path] && !signingRef.current.has(c.image_path),
    )
    if (!todo.length) return undefined
    todo.forEach((c) => signingRef.current.add(c.image_path))
    ;(async () => {
      const updates = {}
      for (const c of todo) {
        const { data } = await supabase.storage
          .from('moments')
          .createSignedUrl(c.image_path, 60 * 60)
        if (data?.signedUrl) updates[c.image_path] = data.signedUrl
        signingRef.current.delete(c.image_path)
      }
      if (alive && Object.keys(updates).length) {
        setSignedUrls((prev) => ({ ...prev, ...updates }))
      }
    })()
    return () => { alive = false }
  }, [captures])

  const mine = useMemo(
    () => captures.find((c) => c.user_id === userId) || null,
    [captures, userId],
  )
  const partner = useMemo(
    () => captures.find((c) => c.user_id !== userId) || null,
    [captures, userId],
  )

  const partnerName = partnerId ? (getDisplayName(partnerId, locale) || '') : ''

  const myImageUrl = mine ? (signedUrls[mine.image_path] || null) : null
  const partnerImageUrl = partner ? (signedUrls[partner.image_path] || null) : null

  const windowCloseTs = prompt ? new Date(prompt.window_closes_at).getTime() : 0
  const msUntilClose = Math.max(0, windowCloseTs - now)
  const windowOpen = !!prompt && msUntilClose > 0

  const submit = useCallback(async (blob) => {
    if (!userId || !prompt) return { error: 'no user or prompt' }
    const up = await uploadMomentImage(userId, prompt.id, blob)
    if (up.error || !up.path) return { error: up.error || 'upload failed' }
    const late = Date.now() > windowCloseTs
    const { error: insErr } = await supabase
      .from('moments_captures')
      .insert({
        prompt_id: prompt.id,
        user_id: userId,
        image_path: up.path,
        late,
      })
    if (insErr) return { error: insErr.message }
    await refetch()
    return { path: up.path }
  }, [userId, prompt, windowCloseTs, refetch])

  return {
    prompt,
    mine,
    partner,
    partnerName,
    myImageUrl,
    partnerImageUrl,
    status: prompt?.status || null,
    windowOpen,
    msUntilClose,
    submit,
    loading,
    refetch,
  }
}
